import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  TOOLTIP_GAP,
  TOOLTIP_WIDTH,
  VIEWPORT_MARGIN,
} from './onboarding.constants';
import classes from './Onboarding.module.scss';

const clampToViewport = (rect, placement, tooltipSize) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const { width: tw, height: th } = tooltipSize;

  if (!rect) {
    return {
      top: Math.max(VIEWPORT_MARGIN, vh / 2 - th / 2),
      left: Math.max(VIEWPORT_MARGIN, vw / 2 - tw / 2),
    };
  }

  let top = 0;
  let left = 0;

  switch (placement) {
    case 'top':
      top = rect.top - th - TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tw / 2;
      break;
    case 'bottom':
      top = rect.top + rect.height + TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tw / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - th / 2;
      left = rect.left - tw - TOOLTIP_GAP;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - th / 2;
      left = rect.left + rect.width + TOOLTIP_GAP;
      break;
    default:
      break;
  }

  if (top < VIEWPORT_MARGIN) {
    top = rect.top + rect.height + TOOLTIP_GAP;
  }
  if (top + th > vh - VIEWPORT_MARGIN) {
    top = Math.max(VIEWPORT_MARGIN, rect.top - th - TOOLTIP_GAP);
  }
  top = Math.min(
    Math.max(VIEWPORT_MARGIN, top),
    Math.max(VIEWPORT_MARGIN, vh - th - VIEWPORT_MARGIN)
  );
  left = Math.min(
    Math.max(VIEWPORT_MARGIN, left),
    Math.max(VIEWPORT_MARGIN, vw - tw - VIEWPORT_MARGIN)
  );

  return { top, left };
};

const OnboardingTooltip = ({
  rect,
  step,
  stepIndex,
  stepCount,
  onSkip,
  onPrev,
  onNext,
}) => {
  const ref = useRef(null);
  const [position, setPosition] = useState({ top: -9999, left: -9999 });

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const size = {
      width: node.offsetWidth || TOOLTIP_WIDTH,
      height: node.offsetHeight || 160,
    };
    setPosition(clampToViewport(rect, step.placement, size));
  }, [rect, step.placement, step.id]);

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === stepCount - 1;

  return (
    <div
      ref={ref}
      className={classes.Tooltip}
      style={{ top: position.top, left: position.left }}
      role="dialog"
      aria-live="polite"
      onClick={(e) => e.stopPropagation()}
    >
      <div className={classes.TooltipHeader}>
        <p className={classes.TooltipCounter}>
          {stepIndex + 1} / {stepCount}
        </p>
        <button
          type="button"
          className={classes.TooltipSkip}
          onClick={onSkip}
        >
          Skip
        </button>
      </div>
      <h3 className={classes.TooltipTitle}>{step.title}</h3>
      <p className={classes.TooltipBody}>{step.body}</p>
      <div className={classes.TooltipActions}>
        <div className={classes.TooltipPrimaryActions}>
          {!isFirst && (
            <button
              type="button"
              className={classes.BtnSecondary}
              onClick={onPrev}
            >
              Back
            </button>
          )}
          <button
            type="button"
            className={classes.BtnPrimary}
            onClick={onNext}
          >
            {isLast ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTooltip;
